import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import AppLayout, { PageHeader } from "../components/AppLayout";
import {
    SeverityChip,
    VerdictChip,
    StatusBadge,
} from "../components/SeverityChip";
import { AuditProvenanceCard, ConfidenceChip } from "../components/AuditProvenance";
import {
    getSystem,
    listDocuments,
    uploadDocument,
    seedDocuments,
    runAudit,
    getAuditJob,
    getRules,
    getRequirements,
    getFindings,
    getTraceability,
    getLatestAudit,
    saveSnapshot,
    api,
} from "../lib/api";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "../components/ui/tabs";
import { toast } from "sonner";
import {
    Upload,
    Play,
    Loader2,
    Sparkles,
    ChevronLeft,
    FileText,
    Download,
    RefreshCw,
    Save,
    Eye,
} from "lucide-react";
import DocumentPreviewModal from "../components/DocumentPreviewModal";
import FindingReviewControls, {
    ReviewStatusPill,
} from "../components/FindingReviewControls";

const docTypeOrder = [
    "SOP",
    "URS",
    "IQ",
    "OQ",
    "PQ",
    "Traceability",
    "Change Control",
    "Deviation",
    "Validation Report",
    "Other",
];

const StatTile = ({ label, value, sub, testid }) => (
    <div
        className="p-6 border-r border-b border-hair bg-white"
        data-testid={testid}
    >
        <div className="overline">{label}</div>
        <div className="heading text-3xl font-medium tracking-tighter leading-none mt-3">
            {value}
        </div>
        {sub && <div className="text-[11px] text-neutral-500 mt-2 mono">{sub}</div>}
    </div>
);

export default function SystemDetail() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get("tab");
    const validTabs = ["documents", "rules", "requirements", "traceability", "findings"];
    const [activeTab, setActiveTab] = useState(
        validTabs.includes(tabFromUrl) ? tabFromUrl : "documents",
    );
    useEffect(() => {
        if (validTabs.includes(tabFromUrl) && tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabFromUrl]);
    const handleTabChange = (v) => {
        setActiveTab(v);
        // Mirror to URL so deep-links and the guided tour can drive the active tab
        const params = Object.fromEntries(searchParams.entries());
        params.tab = v;
        setSearchParams(params, { replace: true });
    };
    const [system, setSystem] = useState(null);
    const [docs, setDocs] = useState([]);
    const [rules, setRules] = useState([]);
    const [reqs, setReqs] = useState([]);
    const [findings, setFindings] = useState([]);
    const [trace, setTrace] = useState([]);
    const [latestAudit, setLatestAudit] = useState(null);
    const [running, setRunning] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const fileRef = useRef();

    const load = async () => {
        const [s, d, r, rq, f, t, a] = await Promise.all([
            getSystem(id),
            listDocuments(id),
            getRules(id),
            getRequirements(id),
            getFindings(id),
            getTraceability(id),
            getLatestAudit(id),
        ]);
        setSystem(s);
        setDocs(d);
        setRules(r);
        setReqs(rq);
        setFindings(f);
        setTrace(t);
        setLatestAudit(a && a.id ? a : null);
    };

    useEffect(() => {
        load();
    }, [id]);

    const handleSeedDocs = async () => {
        setSeeding(true);
        try {
            const r = await seedDocuments(id);
            toast.success(`Loaded ${r.inserted.length} demo documents`);
            load();
        } catch (e) {
            toast.error("Seed failed");
        } finally {
            setSeeding(false);
        }
    };

    const handleRun = async () => {
        if (docs.length === 0) {
            toast.error("Upload or seed documents first");
            return;
        }
        setRunning(true);
        const t = toast.loading("AudRI analyzing — extracting rules, matching evidence…");
        try {
            const job = await runAudit(id);
            // poll
            let status = "running";
            let jobState = job;
            for (let i = 0; i < 90; i++) {
                await new Promise((r) => setTimeout(r, 3000));
                jobState = await getAuditJob(job.id);
                status = jobState.status;
                if (status !== "running") break;
            }
            if (status === "completed") {
                toast.success("Audit complete", { id: t });
                load();
            } else if (status === "failed") {
                toast.error(`Audit failed: ${jobState.error || "unknown"}`, { id: t });
            } else {
                toast.message("Audit still running — results will appear soon", { id: t });
                load();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Audit failed", { id: t });
        } finally {
            setRunning(false);
        }
    };

    const handleUpload = async (files) => {
        if (!files?.length) return;
        setUploading(true);
        try {
            const results = [];
            for (const f of Array.from(files)) {
                const doc = await uploadDocument(id, f, null);
                results.push(doc);
            }
            const storageFailed = results.filter((d) => d?.storage_error);
            if (storageFailed.length) {
                toast.warning(
                    `${results.length} uploaded — ${storageFailed.length} fell back to text-only ` +
                        "(object storage unavailable; previews disabled).",
                    { duration: 7000 },
                );
            } else {
                toast.success(`${files.length} document(s) uploaded`);
            }
            load();
        } catch (e) {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const exportReport = async () => {
        try {
            const url = `${api.defaults.baseURL}/systems/${id}/report.pdf`;
            const res = await fetch(url, {
                method: "GET",
                credentials: "include",
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(
                    text.includes("No completed audit")
                        ? "Run a Periodic Review first, then download the report."
                        : `Download failed (${res.status})`,
                );
            }
            const blob = await res.blob();
            const a = document.createElement("a");
            const dlUrl = URL.createObjectURL(blob);
            a.href = dlUrl;
            a.download = `${system?.name || "audri"}_periodic_review.pdf`;
            a.click();
            URL.revokeObjectURL(dlUrl);
            toast.success("PDF downloaded");
        } catch (e) {
            toast.error(e?.message || "PDF export failed");
        }
    };

    const handleSaveSnapshot = async () => {
        const defaultLabel = `${system?.name} · ${new Date().toLocaleDateString()}`;
        const label = window.prompt("Snapshot label?", defaultLabel);
        if (!label) return;
        try {
            const s = await saveSnapshot(id, label);
            toast.success(`Snapshot saved: ${s.label}`);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Snapshot failed. Run Periodic Review first.",
            );
        }
    };

    if (!system) {
        // Lightweight skeleton that preserves the same structure and
        // data-testid hooks so the guided tour can lock onto its targets
        // immediately after navigation (prevents ~2s blank flash on step 6).
        return (
            <AppLayout>
                <div className="border-b border-hair bg-white">
                    <div className="px-8 md:px-12 py-8">
                        <div className="h-3 w-28 bg-neutral-100 rounded-sm mb-4 animate-pulse" />
                        <div className="h-10 w-72 bg-neutral-100 rounded-sm animate-pulse" />
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 border-l border-hair">
                    {[
                        ["Compliance", "stat-compliance"],
                        ["Coverage", "stat-coverage"],
                        ["Rules", "stat-rules"],
                        ["Findings", "stat-findings"],
                        ["Last review", "stat-lastreview"],
                    ].map(([label, tid]) => (
                        <div
                            key={tid}
                            data-testid={tid}
                            className="p-8 border-r border-b border-hair bg-white"
                        >
                            <div className="overline mb-4 text-neutral-400">
                                {label}
                            </div>
                            <div className="h-8 w-20 bg-neutral-100 rounded-sm animate-pulse" />
                        </div>
                    ))}
                </div>
                <div
                    data-testid="reviewer-status"
                    className="mx-8 md:mx-12 mt-6 border border-hair bg-white p-6"
                >
                    <div className="h-3 w-40 bg-neutral-100 rounded-sm mb-4 animate-pulse" />
                    <div className="h-4 w-full bg-neutral-100 rounded-sm mb-2 animate-pulse" />
                    <div className="h-4 w-3/4 bg-neutral-100 rounded-sm animate-pulse" />
                </div>
                <div className="p-8 md:p-12 flex items-center gap-2 text-neutral-400 text-xs">
                    <Loader2 className="animate-spin" size={14} /> Loading system…
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout>
            <PageHeader
                eyebrow={
                    <Link
                        to="/dashboard"
                        className="inline-flex items-center gap-1 hover:text-[#C2410C]"
                    >
                        <ChevronLeft size={12} /> Dashboard
                    </Link>
                }
                title={system.name}
                sub={`${system.full_name || ""}${
                    system.vendor ? ` · ${system.vendor}` : ""
                }${system.version ? ` v${system.version}` : ""}`}
                right={
                    <div className="flex gap-3 flex-wrap items-center">
                        <StatusBadge value={system.status} />
                        <button
                            onClick={handleSaveSnapshot}
                            disabled={rules.length === 0 && findings.length === 0}
                            data-testid="btn-save-snapshot"
                            className="inline-flex items-center gap-2 border border-hair text-sm px-4 py-2 rounded-sm hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                            title={
                                rules.length === 0 && findings.length === 0
                                    ? "Run a Periodic Review first to create audit data worth snapshotting"
                                    : "Freeze the current audit as a demo snapshot (no LLM cost)"
                            }
                        >
                            <Save size={14} /> Save snapshot
                        </button>
                        <button
                            onClick={exportReport}
                            data-testid="btn-export"
                            className="inline-flex items-center gap-2 border border-hair text-sm px-4 py-2 rounded-sm hover:bg-white"
                            title="Download inspection-ready PDF periodic-review report"
                        >
                            <Download size={14} /> Export PDF
                        </button>
                        <button
                            data-testid="btn-run-audit"
                            onClick={handleRun}
                            disabled={running}
                            className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#9A3412] disabled:opacity-50"
                        >
                            {running ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Play size={14} />
                            )}
                            {running ? "Running AudRI…" : "Run Periodic Review"}
                        </button>
                    </div>
                }
            />

            {/* Stat band */}
            <div className="grid grid-cols-2 md:grid-cols-5 border-l border-hair">
                <StatTile
                    label="Compliance"
                    value={`${system.compliance_score}%`}
                    sub="Overall score"
                    testid="stat-compliance"
                />
                <StatTile
                    label="Coverage"
                    value={`${system.coverage_percent}%`}
                    sub="Rules → evidence"
                    testid="stat-coverage"
                />
                <StatTile
                    label="Rules"
                    value={rules.length}
                    sub="Extracted from SOPs"
                    testid="stat-rules"
                />
                <StatTile
                    label="Findings"
                    value={findings.length}
                    sub={`${
                        findings.filter((f) => f.severity === "Critical").length
                    } critical`}
                    testid="stat-findings"
                />
                <StatTile
                    label="Last review"
                    value={
                        system.last_review_date
                            ? new Date(system.last_review_date).toLocaleDateString()
                            : "—"
                    }
                    sub={`Next: ${
                        system.next_review_date
                            ? new Date(system.next_review_date).toLocaleDateString()
                            : "—"
                    }`}
                    testid="stat-lastreview"
                />
            </div>

            {latestAudit && latestAudit.id && (
                <AuditProvenanceCard audit={latestAudit} onSigned={load} />
            )}

            <div className="p-8 md:p-12">
                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                    <TabsList
                        className="bg-transparent p-0 h-auto border-b border-hair rounded-none w-full justify-start gap-2"
                        data-testid="tabs-list"
                    >
                        {[
                            ["documents", "Documents"],
                            ["rules", "SOP Rules"],
                            ["requirements", "Requirements"],
                            ["traceability", "Traceability"],
                            ["findings", "Findings & CAPA"],
                        ].map(([v, l]) => (
                            <TabsTrigger
                                key={v}
                                value={v}
                                data-testid={`tab-${v}`}
                                className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#C2410C] data-[state=active]:bg-transparent data-[state=active]:shadow-none text-sm px-4 py-3 -mb-[1px]"
                            >
                                {l}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {/* Documents */}
                    <TabsContent value="documents" className="pt-8">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                            <div className="overline">Validation Package</div>
                            <div className="flex gap-2">
                                <input
                                    ref={fileRef}
                                    type="file"
                                    multiple
                                    hidden
                                    onChange={(e) => handleUpload(e.target.files)}
                                    accept=".pdf,.docx,.txt,.md"
                                />
                                <button
                                    data-testid="btn-upload"
                                    onClick={() => fileRef.current?.click()}
                                    disabled={uploading}
                                    className="inline-flex items-center gap-2 border border-hair text-sm px-4 py-2 rounded-sm hover:bg-white disabled:opacity-50"
                                >
                                    {uploading ? (
                                        <Loader2
                                            size={14}
                                            className="animate-spin"
                                        />
                                    ) : (
                                        <Upload size={14} />
                                    )}
                                    Upload (PDF / DOCX / TXT)
                                </button>
                                <button
                                    data-testid="btn-seed-docs"
                                    onClick={handleSeedDocs}
                                    disabled={seeding}
                                    className="inline-flex items-center gap-2 bg-[#0a0a0a] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#262626] disabled:opacity-50"
                                >
                                    {seeding ? (
                                        <Loader2
                                            size={14}
                                            className="animate-spin"
                                        />
                                    ) : (
                                        <Sparkles size={14} />
                                    )}
                                    Load demo pack
                                </button>
                            </div>
                        </div>

                        <div className="border border-hair bg-white">
                            <div className="grid grid-cols-12 px-4 py-3 border-b border-hair overline text-[10px]">
                                <div className="col-span-2">Type</div>
                                <div className="col-span-3">Filename</div>
                                <div className="col-span-1 text-center">Rev</div>
                                <div className="col-span-3">SHA-256</div>
                                <div className="col-span-1 text-right">Size</div>
                                <div className="col-span-2 text-right">Actions</div>
                            </div>
                            {docs.length === 0 && (
                                <div className="p-10 text-center text-neutral-500">
                                    <FileText
                                        className="mx-auto text-neutral-300"
                                        size={32}
                                    />
                                    <p className="text-sm mt-3">
                                        No documents yet. Upload SOP / URS / IQ / OQ /
                                        PQ or load the demo pack.
                                    </p>
                                </div>
                            )}
                            {docs
                                .sort(
                                    (a, b) =>
                                        docTypeOrder.indexOf(a.doc_type) -
                                        docTypeOrder.indexOf(b.doc_type),
                                )
                                .map((d) => (
                                    <div
                                        key={d.id}
                                        className="grid grid-cols-12 px-4 py-3 border-b border-hair last:border-b-0 items-center"
                                        data-testid={`doc-row-${d.filename}`}
                                    >
                                        <div className="col-span-2">
                                            <span className="mono text-[11px] px-2 py-[2px] border border-hair rounded-sm">
                                                {d.doc_type}
                                            </span>
                                        </div>
                                        <div className="col-span-3 text-sm truncate">
                                            {d.filename}
                                            {!d.has_file && (
                                                <span
                                                    className="mono text-[10px] uppercase tracking-wider px-2 py-[2px] border border-[#FDE68A] bg-[#FEF3C7] text-[#854D0E] rounded-sm ml-2"
                                                    title={
                                                        d.storage_error ||
                                                        "Original file not retained — seeded or legacy document. Audit pipeline still works on extracted text; in-browser preview is disabled."
                                                    }
                                                    data-testid={`pill-text-only-${d.filename}`}
                                                >
                                                    text-only
                                                </span>
                                            )}
                                        </div>
                                        <div className="col-span-1 text-center mono text-xs text-neutral-600">
                                            v{d.revision || 1}
                                        </div>
                                        <div
                                            className="col-span-3 mono text-[11px] text-neutral-500 truncate"
                                            title={d.sha256}
                                        >
                                            {d.sha256
                                                ? d.sha256.slice(0, 24) + "…"
                                                : "—"}
                                        </div>
                                        <div className="col-span-1 text-right num text-xs">
                                            {(d.size / 1024).toFixed(1)} KB
                                        </div>
                                        <div className="col-span-2 flex items-center justify-end gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setPreviewDoc(d)}
                                                disabled={!d.has_file}
                                                data-testid={`btn-preview-${d.filename}`}
                                                title={
                                                    d.has_file
                                                        ? "Preview original file"
                                                        : "No original file retained for this document"
                                                }
                                                className="inline-flex items-center gap-1 border border-hair text-[11px] px-2 py-1 rounded-sm hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                            >
                                                <Eye size={12} /> Preview
                                            </button>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </TabsContent>

                    {/* Rules */}
                    <TabsContent value="rules" className="pt-8">
                        <div className="overline mb-4">Extracted Rules</div>
                        {rules.length === 0 ? (
                            <EmptyState
                                text="Run a periodic review to extract rules from SOPs."
                            />
                        ) : (
                            <div className="border border-hair bg-white">
                                {rules.map((r) => (
                                    <div
                                        key={r.id}
                                        className="px-5 py-4 border-b border-hair last:border-b-0"
                                        data-testid={`rule-${r.rule_id}`}
                                    >
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="flex items-center gap-3 min-w-0 flex-wrap">
                                                <span className="mono text-xs text-[#C2410C] font-medium">
                                                    {r.rule_id}
                                                </span>
                                                <span className="mono text-[10px] uppercase tracking-wider px-2 py-[2px] border border-hair rounded-sm">
                                                    {r.category}
                                                </span>
                                                <SeverityChip
                                                    value={
                                                        r.criticality === "Critical"
                                                            ? "Critical"
                                                            : r.criticality === "Minor"
                                                              ? "Minor"
                                                              : "Major"
                                                    }
                                                />
                                                <ConfidenceChip value={r.confidence} />
                                                {r.revision != null && (
                                                    <span className="mono text-[10px] text-neutral-500">
                                                        rev {r.revision}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="mono text-[11px] text-neutral-500">
                                                § {r.section || "—"}
                                            </span>
                                        </div>
                                        <p className="text-sm mt-3 leading-relaxed">
                                            {r.text}
                                        </p>
                                        {r.sop_citation && (
                                            <blockquote className="mt-3 text-[12px] leading-relaxed italic border-l-2 border-[#C2410C] pl-3 text-neutral-600">
                                                <span className="overline not-italic mr-2">
                                                    Source
                                                </span>
                                                “{r.sop_citation}”
                                            </blockquote>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Requirements */}
                    <TabsContent value="requirements" className="pt-8">
                        <div className="overline mb-4">URS Requirements</div>
                        {reqs.length === 0 ? (
                            <EmptyState text="Run a periodic review to extract URS requirements." />
                        ) : (
                            <div className="border border-hair bg-white">
                                {reqs.map((r) => (
                                    <div
                                        key={r.id}
                                        className="px-5 py-4 border-b border-hair last:border-b-0"
                                        data-testid={`req-${r.req_id}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="mono text-xs text-[#C2410C] font-medium">
                                                {r.req_id}
                                            </span>
                                            <span className="mono text-[10px] uppercase tracking-wider px-2 py-[2px] border border-hair rounded-sm">
                                                {r.type}
                                            </span>
                                            <span className="mono text-[10px] tracking-wider text-neutral-500">
                                                {r.priority}
                                            </span>
                                        </div>
                                        <p className="text-sm mt-3 leading-relaxed">
                                            {r.text}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Traceability */}
                    <TabsContent value="traceability" className="pt-8">
                        <div className="overline mb-4">Traceability Matrix</div>
                        {trace.length === 0 ? (
                            <EmptyState text="Run a periodic review to generate the traceability matrix." />
                        ) : (
                            <div className="border border-hair bg-white overflow-x-auto">
                                <div className="grid grid-cols-[110px_110px_110px_120px_1fr_1fr] min-w-[900px]">
                                    <div className="overline text-[10px] px-3 py-3 border-b border-r border-hair">
                                        Rule
                                    </div>
                                    <div className="overline text-[10px] px-3 py-3 border-b border-r border-hair">
                                        URS
                                    </div>
                                    <div className="overline text-[10px] px-3 py-3 border-b border-r border-hair">
                                        Test Case
                                    </div>
                                    <div className="overline text-[10px] px-3 py-3 border-b border-r border-hair">
                                        Verdict
                                    </div>
                                    <div className="overline text-[10px] px-3 py-3 border-b border-r border-hair">
                                        Evidence
                                    </div>
                                    <div className="overline text-[10px] px-3 py-3 border-b border-hair">
                                        Reasoning
                                    </div>

                                    {trace.map((t) => (
                                        <React.Fragment key={t.id}>
                                            <div className="mono text-xs px-3 py-3 border-b border-r border-hair text-[#C2410C]">
                                                {t.rule_id || "—"}
                                            </div>
                                            <div className="mono text-xs px-3 py-3 border-b border-r border-hair">
                                                {t.req_id || "—"}
                                            </div>
                                            <div className="mono text-xs px-3 py-3 border-b border-r border-hair">
                                                {t.test_case_id || "—"}
                                            </div>
                                            <div className="px-3 py-3 border-b border-r border-hair">
                                                <VerdictChip value={t.verdict} />
                                            </div>
                                            <div className="px-3 py-3 border-b border-r border-hair text-xs text-neutral-700">
                                                {t.evidence_found}
                                            </div>
                                            <div className="px-3 py-3 border-b border-hair text-xs text-neutral-600 leading-relaxed">
                                                {t.explanation}
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        )}
                    </TabsContent>

                    {/* Findings */}
                    <TabsContent value="findings" className="pt-8">
                        <div className="overline mb-4">Findings &amp; CAPA</div>
                        {findings.length === 0 ? (
                            <EmptyState text="No findings yet. Run a periodic review." />
                        ) : (
                            <div className="space-y-4">
                                {findings.map((f) => (
                                    <div
                                        key={f.id}
                                        className="border border-hair bg-white p-6"
                                        data-testid={`finding-${f.finding_id}`}
                                    >
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span className="mono text-xs text-[#C2410C] font-medium">
                                                    {f.finding_id}
                                                </span>
                                                <SeverityChip
                                                    value={f.severity}
                                                />
                                                <span className="mono text-[10px] uppercase tracking-wider px-2 py-[2px] border border-hair rounded-sm">
                                                    {f.gap_type}
                                                </span>
                                                {f.rule_id && (
                                                    <span className="mono text-[11px] text-neutral-500">
                                                        ref {f.rule_id}
                                                    </span>
                                                )}
                                            </div>
                                            <ReviewStatusPill
                                                status={f.review_status}
                                            />
                                        </div>
                                        <h3 className="heading text-lg font-medium tracking-tight mt-4">
                                            {f.title}
                                        </h3>
                                        <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
                                            {f.description}
                                        </p>
                                        <div className="mt-5 p-4 bg-[#fff7ed] border border-[#fed7aa] rounded-sm">
                                            <div className="overline mb-2">
                                                Suggested CAPA
                                            </div>
                                            <p className="text-sm leading-relaxed">
                                                {f.capa}
                                            </p>
                                        </div>
                                        <FindingReviewControls
                                            finding={f}
                                            onChange={(updated) =>
                                                setFindings((prev) =>
                                                    prev.map((x) =>
                                                        x.id === f.id
                                                            ? { ...x, ...updated }
                                                            : x,
                                                    ),
                                                )
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
            {previewDoc && (
                <DocumentPreviewModal
                    document={previewDoc}
                    onClose={() => setPreviewDoc(null)}
                />
            )}
        </AppLayout>
    );
}

const EmptyState = ({ text }) => (
    <div className="border border-dashed border-hair bg-white p-16 text-center">
        <RefreshCw className="mx-auto text-neutral-300" size={28} />
        <p className="text-sm text-neutral-500 mt-4">{text}</p>
    </div>
);
